// The shared scale of the rank Components (RankTierChip, RankLineChart).
// A rank value is a place in a list: a number from 1 (the top), 'over' (after
// the last place that the list shows), or null (no value: a gap).
// This file knows nothing about the data behind the places.

export type RankValue = number | 'over' | null;

// The last place of the scale. A place after it is 'over'.
export const RANK_MAX = 500;

// The ticks of the y axis of RankLineChart, and the upper limits of the tiers.
export const RANK_TICKS: readonly number[] = [1, 10, 25, 50, 100, 250, 500];

// One tier of RankTierChip: the upper limit of the band and the Tailwind
// classes of the chip. Each pair of text and fill has a contrast of 4.5:1 or more:
// 8.72, 5.17, 5.74, 7.29, 7.15, 8.01 and 8.33 (WCAG 2.x formula).
export type RankTier = {
	limit: number | 'over',
	className: string,
};

export const RANK_TIERS: readonly RankTier[] = [
	{ limit: 10, className: 'bg-blue-800 text-white' },
	{ limit: 25, className: 'bg-blue-600 text-white' },
	{ limit: 50, className: 'bg-blue-300 text-blue-900' },
	{ limit: 100, className: 'bg-blue-200 text-blue-900' },
	{ limit: 250, className: 'bg-blue-100 text-blue-800' },
	{ limit: 500, className: 'bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-200' },
	{ limit: 'over', className: 'bg-gray-200 text-gray-700' },
];

// The tier of a value, or undefined for null (no value).
export const rankTierOf = (value: RankValue): RankTier | undefined => {
	if (value === null) return undefined;
	if (value === 'over' || value > RANK_MAX) return RANK_TIERS[RANK_TIERS.length - 1];
	return RANK_TIERS.find(tier => tier.limit !== 'over' && value <= tier.limit);
};

// The default colours of the lines of RankLineChart, for the caller that has
// no colours of its own. RANK_EMPHASIS_COLOR is for the one line in front
// (blue-600). RANK_LINE_COLORS is for the other lines, in their order:
// orange-600, cyan-600, yellow-700, pink-600, green-700, violet-700, rose-800,
// and slate-500 with a dash for the 8th line. Each colour has a contrast of
// 3:1 or more on white. After the 8th line, the colours start again.
export const RANK_EMPHASIS_COLOR = '#2563eb';
export const RANK_LINE_COLORS: readonly string[] = [
	'#ea580c', '#0891b2', '#a16207', '#db2777', '#15803d', '#6d28d9', '#9f1239', '#64748b',
];

// The colour and the dash of the line at this index (0 = the first line after the line in front).
export const rankLineStyle = (index: number): { color: string, dashed: boolean } => {
	const i = index % RANK_LINE_COLORS.length;
	return { color: RANK_LINE_COLORS[i], dashed: i === RANK_LINE_COLORS.length - 1 };
};
